import type { MapGeometry, Point, Segment } from "../types";

const clampUnit = (value: number) => Math.max(0, Math.min(1, value));

/** Universal VTT 0.2/0.3 accepts ambient light as a numeric level or RGB/RGBA hex. */
export function parseAmbientLight(value: unknown, fallback = 0.35): number {
  if (value === undefined || value === null || value === "") return fallback;
  if (typeof value === "number") return Number.isFinite(value) ? clampUnit(value) : fallback;
  if (typeof value !== "string") return fallback;

  const text = value.trim();
  const numeric = Number(text);
  if (text !== "" && Number.isFinite(numeric)) return clampUnit(numeric);

  const hex = text.replace(/^#/, "");
  if (!/^(?:[0-9a-f]{6}|[0-9a-f]{8})$/i.test(hex)) return fallback;
  const red = Number.parseInt(hex.slice(0, 2), 16) / 255;
  const green = Number.parseInt(hex.slice(2, 4), 16) / 255;
  const blue = Number.parseInt(hex.slice(4, 6), 16) / 255;
  const alpha = hex.length === 8 ? Number.parseInt(hex.slice(6, 8), 16) / 255 : 1;
  return clampUnit((0.2126 * red + 0.7152 * green + 0.0722 * blue) * alpha);
}

/**
 * DD2VTT 0.2/0.3 map sizes and geometry are already expressed in grid squares.
 * Some non-standard, unversioned exporters instead emit image pixels; infer those
 * only when the reported map size is implausibly large for grid coordinates.
 */
export function coordinateScale(version: string, size: Point, pixelsPerGrid: number): number {
  if (version === "0.2" || version === "0.3") return 1;
  return Math.max(Math.abs(size.x), Math.abs(size.y)) > pixelsPerGrid * 8 ? pixelsPerGrid : 1;
}

/** Return the two line endpoints for standard DD2VTT portals and common variants. */
export function portalEndpoints(portal: Record<string, unknown>): [unknown, unknown] | undefined {
  for (const candidate of [portal.bounds, portal.points, portal.endpoints]) {
    if (Array.isArray(candidate) && candidate.length >= 2) return [candidate[0], candidate[1]];
  }
  const pairs: Array<[unknown, unknown]> = [
    [portal.a, portal.b],
    [portal.start, portal.end],
    [portal.p1, portal.p2],
  ];
  for (const [a, b] of pairs) if (a !== undefined && b !== undefined) return [a, b];
  // A few non-standard exporters place the endpoint array in `position`.
  if (Array.isArray(portal.position) && portal.position.length >= 2) return [portal.position[0], portal.position[1]];
  return undefined;
}

/**
 * Dungeondraft normally exports every coordinate in world-grid space, so the
 * crop origin must be subtracted. Some releases/plugins have emitted object
 * occluders in already-cropped (image-local) coordinates while regular walls
 * still use world coordinates. Pick the convention that places substantially
 * more object points on the exported image, otherwise keep the specification's
 * world-coordinate convention.
 */
export function objectOccluderOrigin(
  lines: unknown,
  declaredOrigin: Point,
  mapSize: Point,
  scale: number,
): { origin: Point; corrected: boolean } {
  if (!Array.isArray(lines) || (declaredOrigin.x === 0 && declaredOrigin.y === 0)) {
    return { origin: declaredOrigin, corrected: false };
  }

  const width = mapSize.x / scale;
  const height = mapSize.y / scale;
  const tolerance = 0.25;
  let declaredInside = 0;
  let localInside = 0;
  let total = 0;

  const readPoint = (value: unknown): Point | undefined => {
    if (Array.isArray(value)) {
      const x = Number(value[0]); const y = Number(value[1]);
      return Number.isFinite(x) && Number.isFinite(y) ? { x, y } : undefined;
    }
    if (value && typeof value === "object") {
      const item = value as Record<string, unknown>;
      const x = Number(item.x); const y = Number(item.y);
      return Number.isFinite(x) && Number.isFinite(y) ? { x, y } : undefined;
    }
    return undefined;
  };

  const inside = (point: Point, origin: Point) => {
    const x = (point.x - origin.x) / scale;
    const y = (point.y - origin.y) / scale;
    return x >= -tolerance && x <= width + tolerance && y >= -tolerance && y <= height + tolerance;
  };

  for (const line of lines) {
    if (!Array.isArray(line)) continue;
    for (const value of line) {
      const candidate = readPoint(value);
      if (!candidate) continue;
      total += 1;
      if (inside(candidate, declaredOrigin)) declaredInside += 1;
      if (inside(candidate, { x: 0, y: 0 })) localInside += 1;
    }
  }

  if (total > 0 && localInside / total >= 0.8 && localInside - declaredInside >= Math.max(2, total * 0.15)) {
    return { origin: { x: 0, y: 0 }, corrected: true };
  }
  return { origin: declaredOrigin, corrected: false };
}

const LOCAL_OBJECT_WARNING = "Object occluders used image-local coordinates and were aligned to the cropped map.";
const MIXED_CROP_WARNING = "Off-map geometry groups were aligned to the cropped map.";

function pointInsideMap(point: Point, geometry: MapGeometry, tolerance = 0.25): boolean {
  return point.x >= -tolerance && point.x <= geometry.width + tolerance && point.y >= -tolerance && point.y <= geometry.height + tolerance;
}

function shifted(point: Point, geometry: MapGeometry): Point {
  const origin = geometry.source.originalOrigin ?? { x: 0, y: 0 };
  const version = geometry.source.version ?? "unknown";
  const pixelsPerGrid = geometry.source.pixelsPerGrid ?? 1;
  const scale = version === "0.2" || version === "0.3" || Math.max(Math.abs(origin.x), Math.abs(origin.y)) <= Math.max(geometry.width, geometry.height) * 2
    ? 1 : pixelsPerGrid;
  return { x: point.x + origin.x / scale, y: point.y + origin.y / scale };
}

/** Repair geometry imported before image-local crop-coordinate detection existed. */
export function repairCroppedGeometry(geometry: MapGeometry): { geometry: MapGeometry; changed: boolean } {
  const origin = geometry.source.originalOrigin;
  if (!origin || (origin.x === 0 && origin.y === 0)) return { geometry, changed: false };

  const repairGroups = (segments: Segment[]): { segments: Segment[]; changed: boolean } => {
    const groups = new Map<string, Segment[]>();
    for (const segment of segments) {
      const match = segment.id.match(/^(?:wall|object)-(\d+)-/);
      const key = match?.[1] ?? segment.id;
      groups.set(key, [...(groups.get(key) ?? []), segment]);
    }
    let anyChanged = false;
    const repaired = segments.map((segment) => {
      const match = segment.id.match(/^(?:wall|object)-(\d+)-/);
      const group = groups.get(match?.[1] ?? segment.id) ?? [segment];
      const points = group.flatMap((item) => [item.a, item.b]);
      const currentInside = points.filter((item) => pointInsideMap(item, geometry)).length;
      const movedInside = points.filter((item) => pointInsideMap(shifted(item, geometry), geometry)).length;
      if (currentInside / points.length < 0.2 && movedInside / points.length >= 0.8) {
        anyChanged = true;
        return { ...segment, a: shifted(segment.a, geometry), b: shifted(segment.b, geometry) };
      }
      return segment;
    });
    return { segments: repaired, changed: anyChanged };
  };

  const repairedWalls = repairGroups(geometry.walls);
  let walls = repairedWalls.segments;
  let objectWalls = geometry.objectWalls;
  let changed = false;
  let objectChanged = false;
  let groupChanged = repairedWalls.changed;
  if (repairedWalls.changed) changed = true;
  const alreadyCorrected = geometry.source.warnings.includes(LOCAL_OBJECT_WARNING);
  if (!alreadyCorrected && objectWalls.length > 0) {
    const points = objectWalls.flatMap((segment) => [segment.a, segment.b]);
    const currentInside = points.filter((item) => pointInsideMap(item, geometry)).length;
    const shiftedInside = points.filter((item) => pointInsideMap(shifted(item, geometry), geometry)).length;
    if (shiftedInside / points.length >= 0.8 && shiftedInside - currentInside >= Math.max(2, points.length * 0.15)) {
      objectWalls = objectWalls.map((segment): Segment => ({ ...segment, a: shifted(segment.a, geometry), b: shifted(segment.b, geometry) }));
      changed = true; objectChanged = true;
    }
  }
  if (!alreadyCorrected && !objectChanged) {
    const repairedObjects = repairGroups(objectWalls);
    objectWalls = repairedObjects.segments;
    if (repairedObjects.changed) { changed = true; objectChanged = true; groupChanged = true; }
  }

  const portals = geometry.portals.map((portal) => {
    const movedA = shifted(portal.a, geometry); const movedB = shifted(portal.b, geometry);
    const currentInside = Number(pointInsideMap(portal.a, geometry)) + Number(pointInsideMap(portal.b, geometry));
    const movedInside = Number(pointInsideMap(movedA, geometry)) + Number(pointInsideMap(movedB, geometry));
    if (currentInside === 0 && movedInside === 2) { changed = true; return { ...portal, a: movedA, b: movedB }; }
    return portal;
  });
  const lights = geometry.lights.map((light) => {
    const moved = shifted(light.position, geometry);
    if (!pointInsideMap(light.position, geometry) && pointInsideMap(moved, geometry)) { changed = true; return { ...light, position: moved }; }
    return light;
  });

  if (!changed) return { geometry, changed: false };
  let warnings = objectChanged && !alreadyCorrected ? [...geometry.source.warnings, LOCAL_OBJECT_WARNING] : geometry.source.warnings;
  if (groupChanged && !warnings.includes(MIXED_CROP_WARNING)) warnings = [...warnings, MIXED_CROP_WARNING];
  return { geometry: { ...geometry, walls, objectWalls, portals, lights, source: { ...geometry.source, warnings } }, changed: true };
}
